export enum validationType {
  CODE = "code",
  OTHER = "other",
  SPACE = "space",
  NAME = "name",
  /** Letters, numbers, forward slash; no spaces (e.g. room location code). */
  SLASH_CODE = "slashCode",
  /** Letters, numbers, spaces, and forward slash (e.g. room location name). */
  SLASH_NAME = "slashName",
  /** Like OTHER (letters, numbers, spaces) but also allows forward slash. */
  OTHER_SLASH = "otherSlash",
  /** Alphanumeric + special characters; no spaces or line breaks. */
  NOSPACE = "nospace",
}

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phoneRegex = /^\d{10,15}$/;
const alphanumericRegex = /^[a-zA-Z0-9 ]+$/;
const codeRegex = /^[a-zA-Z0-9]+$/;
const spaceRegex = /^[a-zA-Z0-9 ]+$/;
const nameOrDescriptionRegex = /^[^\n\r]+$/;
const slashCodeRegex = /^[a-zA-Z0-9/]+$/;
const slashNameRegex = /^[a-zA-Z0-9/ ]+$/;
const otherSlashRegex = /^[a-zA-Z0-9/ ]+$/;
const noSpaceRegex = /^\S+$/;

export function validateEmail(email: string) {
  return emailRegex.test(email);
}

export function validatePhoneNumber(phoneNumber: string) {
  const digitsOnly = phoneNumber.replace(/\D/g, "");
  return phoneRegex.test(digitsOnly) && phoneNumber.length === 10;
}

export function validateString(
  str: string | null,
  maxLength: number,
  validation: validationType,
) {
  if (typeof str !== "string") {
    return false;
  }

  if (validation === validationType.CODE) {
    if (!codeRegex.test(str)) {
      return false;
    }
  } else if (validation === validationType.SPACE) {
    if (!spaceRegex.test(str)) {
      return false;
    }
  } else if (validation === validationType.NAME) {
    if (!nameOrDescriptionRegex.test(str)) {
      return false;
    }
  } else if (validation === validationType.SLASH_CODE) {
    if (!slashCodeRegex.test(str)) {
      return false;
    }
  } else if (validation === validationType.SLASH_NAME) {
    if (!slashNameRegex.test(str)) {
      return false;
    }
  } else if (validation === validationType.OTHER_SLASH) {
    if (!otherSlashRegex.test(str)) {
      return false;
    }
  } else if (validation === validationType.NOSPACE) {
    if (!noSpaceRegex.test(str)) {
      return false;
    }
  } else {
    if (!alphanumericRegex.test(str)) {
      return false;
    }
  }

  return str.length <= maxLength;
}

/** Like validateString, with an optional minimum length (inclusive). */
export function validateStringRange(
  str: string | null,
  minLength: number,
  maxLength: number,
  validation: validationType,
) {
  if (!validateString(str, maxLength, validation)) {
    return false;
  }
  return (str as string).length >= minLength;
}

export function validateSpace(
  str: string,
  maxLength: number,
  validation: validationType,
): boolean {
  if (typeof str !== "string") {
    return false;
  }

  let regex: RegExp;

  if (validation === validationType.CODE) {
    regex = codeRegex;
  } else if (validation === validationType.NAME) {
    regex = nameOrDescriptionRegex;
  } else if (validation === validationType.SPACE) {
    regex = spaceRegex;
  } else if (validation === validationType.SLASH_CODE) {
    regex = slashCodeRegex;
  } else if (validation === validationType.SLASH_NAME) {
    regex = slashNameRegex;
  } else if (validation === validationType.OTHER_SLASH) {
    regex = otherSlashRegex;
  } else if (validation === validationType.NOSPACE) {
    regex = noSpaceRegex;
  } else {
    regex = alphanumericRegex;
  }

  if (!regex.test(str)) {
    return false;
  }

  if (str.length > maxLength) {
    return false;
  }

  return true;
}

export function validateIntegerLength(int: number, maxLength: number) {
  return int.toString().length <= maxLength;
}

export const validationLength = {
  propertyName: 20,
  buildingCode: 15,
  buildingName: 50,
  roomFeatureCode: 15,
  roomFeatureName: 50,
  roomTypeCode: 15,
  roomTypeName: 50,
  houseKeepingCode: 15,
  houseKeepingName: 50,
  departmentName: 50,
  categoryName: 50,
  originCode: 15,
  originName: 50,
  /**
   * SnC — code allows spaces + special chars (use validationType.NAME).
   * Used by call types, cancel codes, contact/arrival/etc.
   */
  sncLooseCode15: 15,
  /** SnC — name allows spaces + special chars (use validationType.NAME). */
  sncLooseName50: 50,
  /** SnC Cancellation Policy description. */
  sncCancellationPolicyDesc: 500,
  /** SnC Holiday/Hot Date code — alphanumeric, no spaces/special. */
  sncHolidayCode: 15,
  /** SnC Holiday/Hot Date name — spaces + special allowed. */
  sncHolidayName: 50,
  /** SnC Holiday number of nights — max digits. */
  sncHolidayNights: 3,
  /** SnC Function Room Fee Type code — spaces + special allowed. */
  feeTypeCode: 8,
  /** SnC Function Room Fee Type name — spaces + special allowed. */
  feeTypeName: 50,
  /** SnC Function Type code — alphanumeric, no spaces/special. */
  sncFunctionTypeCode: 4,
  /** SnC Function Type name — spaces + special allowed. */
  sncFunctionTypeName: 50,
  /** SnC Function Room code — alphanumeric, no spaces/special. */
  sncFunctionRoomCode: 15,
  /** SnC Function Room name — spaces + special allowed. */
  sncFunctionRoomName: 50,
  /** SnC Menu Item code — alphanumeric, no spaces/special. */
  sncMenuItemCodeMin: 3,
  sncMenuItemCodeMax: 50,
  /** SnC Menu Item name — spaces + special allowed. */
  sncMenuItemNameMin: 3,
  sncMenuItemNameMax: 100,
  /** SnC Setup/Beverage/Misc/AV code — alphanumeric, no spaces/special. */
  sncCatalogCode: 128,
  /** SnC Setup/Beverage/Misc/AV name — spaces + special allowed. */
  sncCatalogName: 300,
  /** SnC Managers — first/last name; alphanumeric + special, no spaces. */
  sncManagerName: 25,
  /** SnC Managers — mapping/manager code; alphanumeric + special, no spaces. */
  sncManagerCode: 250,
  /** SnC Room and Tax / Incidentals code — spaces + special allowed. */
  sncRoomTaxCode: 8,
  /** SnC Room and Tax / Incidentals name — spaces + special allowed. */
  sncRoomTaxName: 50,
  taxExemptCode: 6,
  taxType: 6,
  roomTypeDefinitionCode: 15,
  roomTypeDefinitionName: 50,
  resortFee: 20,
  postTypeNumber: 15,
  postTypeName: 50,
  defaultAmount: 9,
  defaultFolio: 3,
  defaultMasterFolio: 3,
  ledgerNumber: 50,
  paymentCode: 8,
  paymentName: 50,
  paymentCreditLimit: 12,
  centralReservation: 15,
  guestTypeCode: 15,
  guestTypeName: 50,
  preAuthAmount: 12,
  customerSegment1: 8,
  customerSegment2: 8,
  customerDescription: 50,
  blockCode: 15,
  blockName: 25,
  playerType: 50,
  rateType: 50,
  reservationGuestNotesText: 500,
  courseName: 50,
  tournamentName: 25,
  roleCode: 10,
  roleName: 25,
  /** Retail Role Setup — role name (special characters allowed); max 50. */
  retailRoleName: 50,
  outletName: 50,
  categoryGroup: 25,
  spaCategoryGroup: 25,
  golfCategoryName: 25,
  subCategoryName: 25,
  taxName: 25,
  spaTaxName: 25,
  discountTypes: 50,
  cancellationPolicy: 125,
  quickSaleCategory: 25,
  vendorType: 50,
  spaVendorType: 50,
  spaDiscountTypes: 128,
  spaQuickSaleCategory: 25,
  spaDiscountReason: 50,
  spaVendorSetupCode: 15,
  spaVendorSetupName: 50,
  spaVendorSetupFirstName: 50,
  spaVendorSetupLastName: 50,
  discountReason: 50,
  cancelReason: 25,
  cancelReasonDescription: 250,
  lessonLocation: 25,
  templateCode: 15,
  templateName: 25,
  vendorCode: 15,
  vendorName: 50,
  conditionCode: 10,
  conditionName: 40,
  conditionDescription: 200,
  noShowReasonDescription: 250,
  lostDeniedDescription: 250,
  logtypeDescription: 40,
  addOnName: 30,
  breakTypeCode: 10,
  breakTypeName: 40,
  packageCode: 10,
  packageDescription: 50,
  spaGuestTypeCode: 10,
  spaGuestTypeName: 50,
  spaPriceTypeCode: 10,
  spaPriceTypeDescription: 50,
  spaPaymentMethod: 50,
  equipmentCode: 10,
  equipmentName: 40,
  serviceGroupCode: 10,
  serviceGroupName: 40,
  serviceLocationCode: 10,
  serviceLocationName: 40,
  serviceLocationCapacity: 3,
  serviceCode: 10,
  serviceDescription: 40,
  staffCode: 10,
  firstName: 20,
  lastName: 20,
  /** Retail User Setup — User ID (letters and numbers only; max 10). */
  retailUserId: 10,
  /** Retail User Setup — new password (line breaks not allowed). */
  retailUserSetupPassword: 50,
  /** Retail User Setup — Quick ID digits only. */
  retailUserSetupQuickIdDigits: 10,
  aka: 20,
  itemDescription: 50,
  arrivalTypeCode: 15,
  arrivalTypeName: 50,
};

/** Minimum characters for text fields (discount reason, discount type, lesson location, etc.). */
export const MIN_TEXT_FIELD_LENGTH = 3;

/** @deprecated Use MIN_TEXT_FIELD_LENGTH */
export const MIN_DISCOUNT_REASON_FIELD_LENGTH = MIN_TEXT_FIELD_LENGTH;

/** Golf Player Types — max days out start. */
export const MAX_GOLF_PLAYER_TYPE_DAYS_OUT_START = 365;

/** Golf Player Types — max days out end. */
export const MAX_GOLF_PLAYER_TYPE_DAYS_OUT_END = 1826;

export function validateTextFieldWithMinLength(
  str: string | null,
  maxLength: number,
  validation: validationType,
  minLength: number = MIN_TEXT_FIELD_LENGTH,
): boolean {
  if (typeof str !== "string" || str.trim().length < minLength) {
    return false;
  }

  return validateString(str, maxLength, validation);
}

export function validateDiscountReasonField(str: string | null): boolean {
  return validateTextFieldWithMinLength(
    str,
    validationLength.discountReason,
    validationType.SPACE,
  );
}

export function validateDiscountTypeField(str: string | null): boolean {
  return validateTextFieldWithMinLength(
    str,
    validationLength.discountTypes,
    validationType.SPACE,
  );
}

export function validateLessonLocationField(str: string | null): boolean {
  return validateTextFieldWithMinLength(
    str,
    validationLength.lessonLocation,
    validationType.SPACE,
  );
}

export function validateVendorTypeField(str: string | null): boolean {
  return validateTextFieldWithMinLength(
    str,
    validationLength.vendorType,
    validationType.SPACE,
  );
}

export function validateVendorCodeField(str: string | null): boolean {
  return validateTextFieldWithMinLength(
    str,
    validationLength.vendorCode,
    validationType.CODE,
  );
}

export function validateVendorNameField(str: string | null): boolean {
  return validateTextFieldWithMinLength(
    str,
    validationLength.vendorName,
    validationType.SPACE,
  );
}

export function validateOptionalTextFieldWithMinLength(
  str: string | null,
  maxLength: number,
  validation: validationType,
): boolean {
  if (str === null || String(str).trim() === "") {
    return true;
  }

  return validateTextFieldWithMinLength(str, maxLength, validation);
}

export const validationMessages = {
  propertyName: "Enter a property name",
  propertyNameLimit:
    "Enter property name with max 25 characters and no special characters",
  phoneNumber: "Enter a 10-digit phone number",
  email: "Enter a correct E-Mail ID",
  buildingCode:
    "Enter a code with max 15 characters including no spaces and special characters",
  buildingName: "Enter a name with max 50 characters and no special characters",
  roomFeatureCode:
    "Enter a code with max 15 characters including no spaces and special characters",
  roomFeatureName:
    "Enter a name with max 50 characters and no special characters",
  roomLocationCode:
    "Enter a code with max 15 characters; letters, numbers and / only; no spaces or other special characters",
  roomLocationName:
    "Enter a name with max 50 characters; letters, numbers, spaces and / only; no other special characters",
  houseKeepingCode:
    "Enter a code with max 15 characters including no spaces and special characters",
  houseKeepingName:
    "Enter a name with max 50 characters and no special characters",
  departmentName:
    "Enter a name with max 50 characters and no special characters",
  categoryName:
    "Enter a name with max 50 characters including no spaces and special characters",
  originCode:
    "Enter a code with max 15 characters including no spaces and special characters",
  originName: "Enter a name with max 50 characters and no special characters",
  sncLooseCode15:
    "Enter a code with max 15 characters; spaces and special characters allowed; line breaks are not allowed",
  sncLooseName50:
    "Enter a name with max 50 characters; spaces and special characters allowed; line breaks are not allowed",
  sncCancellationPolicyDesc:
    "Enter cancellation policy text with max 500 characters; spaces and special characters allowed; line breaks are not allowed",
  sncHolidayCode:
    "Enter a code with max 15 characters; letters and numbers only; no spaces or special characters",
  sncHolidayName:
    "Enter a name with max 50 characters; spaces and special characters allowed; line breaks are not allowed",
  sncHolidayNights: "Enter number of nights with max 3 digits",
  feeTypeCode:
    "Enter a code with max 8 characters; spaces and special characters allowed; line breaks are not allowed",
  feeTypeName:
    "Enter a name with max 50 characters; spaces and special characters allowed; line breaks are not allowed",
  sncFunctionTypeCode:
    "Enter a code with max 4 characters; letters and numbers only; no spaces or special characters",
  sncFunctionTypeName:
    "Enter a name with max 50 characters; spaces and special characters allowed; line breaks are not allowed",
  sncFunctionRoomCode:
    "Enter a code with max 15 characters; letters and numbers only; no spaces or special characters",
  sncFunctionRoomName:
    "Enter a name with max 50 characters; spaces and special characters allowed; line breaks are not allowed",
  sncMenuItemCode:
    "Enter a code with 3–50 characters; letters and numbers only; no spaces or special characters",
  sncMenuItemName:
    "Enter a name with 3–100 characters; spaces and special characters allowed; line breaks are not allowed",
  sncCatalogCode:
    "Enter a code with max 128 characters; letters and numbers only; no spaces or special characters",
  sncCatalogName:
    "Enter a name with max 300 characters; spaces and special characters allowed; line breaks are not allowed",
  sncManagerName:
    "Enter a name with max 25 characters; letters, numbers and special characters allowed; no spaces",
  sncManagerCode:
    "Enter a manager code with max 250 characters; letters, numbers and special characters allowed; no spaces",
  sncRoomTaxCode:
    "Enter a code with max 8 characters; spaces and special characters allowed; line breaks are not allowed",
  sncRoomTaxName:
    "Enter a name with max 50 characters; spaces and special characters allowed; line breaks are not allowed",
  taxType: "Enter a tax type with max 6 characters and no spaces",
  taxExemptCode: "Enter a code with max 6 characters with no spaces",
  taxExemptName: "Enter the tax exempt name",
  roomTypeDefinitionCode:
    "Enter a code with max 15 characters including no spaces and special characters",
  roomTypeDefinitionName:
    "Enter a name with max 50 characters and no special characters",
  resortFee: "Enter the price within 20 digits",
  postTypeNumber: "Enter a post type number with max 15 digits",
  postTypeName: "Enter a name with max 50 characters and no special characters",
  defaultAmount: "Enter a amount within 9 digits",
  defaultFolio: "Enter a folio length with max 3 digits",
  defaultMasterFolio: "Enter a folio length with max 3 digits",
  ledgerNumber: "Enter a ledger number with max 50 characters",
  paymentCode:
    "Enter a code length with max 8 characters including no spaces and special characters",
  paymentName:
    "Enter a name length with max 50 characters and no special characters",
  paymentCreditLimit: "Enter a limit with max 12 characters",
  centralReservation: "Enter a reservation length with max 15 characters",
  guestTypeCode:
    "Enter a guest code with max 15 characters including no spaces and special characters",
  guestTypeName:
    "Enter a guest name with max 50 characters and no special characters",
  preAuthAmount: "Enter a pre auth amount with max 12 digits",
  customerSegment1:
    "Enter a segment with max 8 characters including no spaces and special characters",
  customerSegment2:
    "Enter a segment with max 8 characters including no spaces and special characters",
  customerDescription:
    "Enter a description with max 50 characters and no special characters",
  blockCode:
    "Enter a block code with max 15 characters including no spaces and special characters",
  blockName:
    "Max 25 characters; letters, numbers and spaces only; no special characters",
  playerType:
    "Max 50 characters; letters, numbers and spaces only; no special characters",
  daysOutStartMax: "Days out start cannot be more than 365.",
  daysOutEndMax: "Days out end cannot be more than 1826.",
  rateType:
    "Max 50 characters; letters, numbers and spaces only; no special characters",
  courseName:
    "Enter a course name length with max 50 characters and no special characters",
  tournamentName:
    "Enter a name length with max 25 characters and no special characters",
  roleCode:
    "Enter a block code with max 10 characters including no spaces and special characters",
  roleName:
    "Max 25 characters; letters, numbers and spaces only; no special characters",
  /** Retail Role Setup — role code (special characters allowed, no newlines); max `validationLength.roleCode`. */
  retailRoleSetupCode:
    "Enter a role code with max 10 characters; line breaks are not allowed",
  /** Retail Role Setup — role name (special characters allowed, no newlines); max `validationLength.retailRoleName`. */
  retailRoleSetupName:
    "Enter a role name with max 50 characters; line breaks are not allowed",
  outletName:
    "Enter an outlet name with max 50 characters; letters, numbers and spaces only; no special characters",
  categoryGroup:
    "Max 25 characters; letters, numbers and spaces only; no special characters",
  spaCategoryGroup:
    "Enter a category group with max 25 characters, letters, numbers and spaces only",
  golfCategoryName:
    "Enter a category name length with max 25 characters and no special characters",
  /** Retail Categories — category name allows special characters (no newlines); max length same as `golfCategoryName`. */
  retailCategoryName:
    "Enter a category name with max 25 characters; line breaks are not allowed",
  /** Retail Sub Categories — each level allows special characters (no newlines); max length same as `subCategoryName`. */
  retailSubCategoryNameLevel:
    "Enter a sub category name with max 25 characters; line breaks are not allowed",
  subCategoryName:
    "Max 25 characters; letters, numbers and spaces only; no special characters",
  taxName:
    "Enter tax name with max 25 characters, letters, numbers and spaces only",
  spaTaxName:
    "Enter tax name with max 25 characters, letters, numbers and spaces only",
  discountTypes:
    "Min 3 characters; max 50 characters; letters, numbers and spaces only; no special characters",
  discountTypeMinLength: "Discount type must be at least 3 characters.",
  cancellationPolicy:
    "Enter cancellation policy text with max 125 characters and no special characters",
  quickSaleCategory:
    "Max 25 characters; letters, numbers and spaces only; no special characters",
  /** Retail Quick Sale Category — special characters allowed (no newlines); max 25. */
  retailQuickSaleCategoryName:
    "Enter a quick sale category with max 25 characters; line breaks are not allowed",
  vendorType:
    "Min 3 characters; max 50 characters; letters, numbers and spaces only; no special characters",
  vendorTypeMinLength: "Vendor type must be at least 3 characters.",
  /** Retail Vendor Types — allows special characters (no newlines); max length same as `vendorType`. */
  retailVendorTypeName:
    "Enter a vendor type with max 50 characters; line breaks are not allowed",
  discountReason:
    "Min 3 characters; max 50 characters; letters, numbers and spaces only; no special characters",
  discountReasonMinLength: "Discount reason must be at least 3 characters.",
  /** Retail Discount Reasons — allows special characters (no newlines); max length same as `discountReason`. */
  retailDiscountReasonName:
    "Enter a discount reason with max 50 characters; line breaks are not allowed",
  cancelReason:
    "Enter cancel reason with max 25 characters and no special characters",
  lessonLocation:
    "Min 3 characters; max 25 characters; letters, numbers and spaces only; no special characters",
  lessonLocationMinLength: "Lesson location must be at least 3 characters.",
  templateCode:
    "Enter template code with max 15 characters including no spaces and special characters",
  templateName:
    "Enter template name with max 25 characters and no special characters",
  vendorCode:
    "Min 3 characters; max 15 characters; letters and numbers only; no spaces or special characters",
  vendorCodeMinLength: "Vendor code must be at least 3 characters.",
  vendorName:
    "Min 3 characters; max 50 characters; letters, numbers and spaces only; no special characters",
  vendorNameMinLength: "Vendor name must be at least 3 characters.",
  /** Retail Vendor Setup — vendor name (special characters allowed; no newlines). */
  retailVendorSetupVendorName:
    "Enter vendor name with max 50 characters; line breaks are not allowed",

  spaDiscountTypes:
    "Enter discount type with max 128 characters, letters, numbers and spaces only",
  spaQuickSaleCategory:
    "Enter a quick sale category with max 25 characters, letters, numbers, spaces and special characters allowed",
  spaVendorType:
    "Enter vendor type with max 50 characters, letters, numbers, spaces and special characters allowed",
  spaDiscountReason:
    "Enter discount reason with max 50 characters, letters, numbers, spaces and special characters allowed",
  spaVendorSetupCode:
    "Enter vendor code with max 15 characters, letters and numbers only, no spaces or special characters",
  spaVendorSetupName:
    "Enter vendor name with max 50 characters, spaces and special characters allowed",
  spaVendorSetupFirstName:
    "Enter first name with max 50 characters, letters, numbers and spaces only",
  spaVendorSetupLastName:
    "Enter last name with max 50 characters, letters, numbers and spaces only",
  cancelReasonDescription: "Enter cancel reason with max 250 characters",
  conditionCode:
    "Enter condition code with no spaces and special characters and max length of 10 characters",
  conditionName: "Enter condtion Name with Alphanumeric and 40 characters",
  noShowReasonDescription: "Enter no show reason with maximum 250 characters",
  conditionDescription:
    "Enter the condition Description with maximum 250 characters",
  lostDeniedDescription:
    "Enter lost and denied description with maxinum 250 characters",
  logtypeDescription: " Enter log type name with maxinum 40 characters",
  addOnName: "Enter add on name with maxinum 30 characters",
  breakTypeCode:
    "Enter breaktype code with no spaces and special characters and max length of 10 characters",
  breakTypeName: "Enter break type name with maxinum 40 characters",
  packageCode:
    "Enter package code with no spaces and special characters and max length of 10 characters ",
  packageDescription: "Enter package Description with maximum 50 characters",
  spaGuestTypeCode:
    "Enter guest type code with no spaces and special characters and max length of 10 characters ",
  spaGuestTypeName: "Enter guest type name with maximum 50 characters",
  spaPriceTypeCode:
    "Enter price type code with no spaces and special characters and max length of 10 characters ",
  spaPriceTypeDescription:
    "Enter price type description with maximum 50 characters",
  spaPaymentMethod: "Enter payment method name with maximum 50 characters",
  equipmentCode:
    "Enter equipment code with no spaces and special characters and max length of 10 characters",
  equipmentName: "Enter equpiment name with maximum 40 characters",
  serviceGroupCode:
    "Enter service group code with no spaces and special characters and max length of 10 characters",
  serviceGroupName: "Enter service group name with maximum 40 characters",
  serviceLocationCode:
    "Enter service location code with no spaces and special characters and max length of 10 characters",
  serviceLocationName: "Enter service location name with maximum 40 characters",
  serviceLocationCapacity:
    "Capacity cannot be greater than 3 digits (maximum 999).",
  serviceCode:
    "Enter service code with no spaces and special characters and max length of 10 characters",
  serviceDescription: "Enter service Description with maximum 40 characters",
  staffCode:
    "Enter service code with no spaces and special characters and max length of 10 characters ",
  firstName: "Enter first name with maximum 20 characters",
  firstNameMinLength: "First name must be at least 3 characters.",
  lastName: "Enter last name with maximum 20 characters",
  lastNameMinLength: "Last name must be at least 3 characters.",
  aka: "Enter aka with maximum 20 characters",
  itemDescription: "Enter item description with maximum 50 characters",
  arrivalTypeCode:
    "Enter arrival type code, spaces and special characters allowed and max length of 15 characters",
  arrivalTypeName:
    "Enter arrival type name, spaces and special characters allowed and max length of 15 characters",

  duplicateCode: "This code already exists.",
  duplicateName: "This name already exists.",

  /** Retail Items — item number (digits only; max `validationLength.postTypeNumber`). */
  retailRetailItemNumberInvalid:
    "Enter digits only for item number (max 15 characters)",
  /** Retail Items — item description (`validationType.SPACE`, max `validationLength.itemDescription`). */
  retailRetailItemDescriptionInvalid:
    "Max 50 characters; letters, numbers and spaces only; no special characters",
  /** Retail Items — bar code must match item number when bar code is entered. */
  retailRetailItemBarCodeMismatch:
    "When provided, Bar Code must match Item Number (digits only)",
  retailRetailItemQuantityInvalid: "Enter whole numbers (digits only)",

  /** Retail User Setup — User ID (`validationType.CODE`, max `validationLength.retailUserId`). */
  retailUserSetupUserId:
    "Enter a user ID with max 10 characters; letters and numbers only; no spaces or special characters",
  retailUserSetupFirstName:
    "Max 20 characters; letters, numbers and spaces only; no special characters",
  retailUserSetupLastName:
    "Max 20 characters; letters, numbers and spaces only; no special characters",
  retailUserSetupNewPassword:
    "Enter a password with max 50 characters; line breaks are not allowed",
  retailUserSetupOutletAccess:
    "Enter outlet access with max 50 characters; line breaks are not allowed",
  retailUserSetupQuickId: "Enter up to 10 digits only",
  retailUserSetupCommissionClass:
    "Enter commission class with max 50 characters; line breaks are not allowed",
  retailUserSetupApplicationAccess:
    "Max 50 characters; letters, numbers and spaces only; no special characters",
  retailUserSetupLogOffMinutes: "Enter up to 4 digits only",

  fieldRequired: "This field is required.",
  endDateBeforeStart: "Range cannot be less than the from date",
  retailTaxValueInvalid: "Enter a valid number.",

  /** SnC Sales & Catering — append to field label for helper text, e.g. `Arrival Type Code${sncMandatorySuffix}` */
  sncMandatorySuffix: " is mandatory",
  sncAllMandatory: "Please enter all mandatory fields.",
  /** Block save / excel when demo sample_test_agilysys values are present. */
  sampleTestNotAllowed:
    "Do not use sample data. Those are for demo purposes only!",
  isoDate: "Enter a valid date (YYYY-MM-DD).",
};
